import { useState, useCallback } from "react"
import { createPortal } from "react-dom"
import cn from "classnames"
import useWindowSize from "utilities/useWindowSize"
import usePortalModal from "utilities/usePortalModal"
import PawprintArrow from "public/assets/pawprint-arrow.svg"
import PortfolioModal from "components/DynamicComponent/molecules/GroomingPortfolioSection/PortfolioModal"
import GroomerCard from "components/DynamicComponent/molecules/GroomingPortfolioSection/GroomerCard"

export default function GroomingPortfolioSection({ blok: { team_members, heading } }) {
  const { width } = useWindowSize()
  const visibleSlides = useCallback(() => {
    if (width >= 1024 && team_members.length >= 4) return 4
    if (width >= 1280 && team_members.length >= 3) return 3
    if (width >= 640 && team_members.length >= 2) return 2
    return 1
  }, [team_members, width])

  const [currentSlide, setSlide] = useState(0)

  const [selectedImages, setSelectedImages] = useState([])

  const imageHashes = team_members.reduce(
    (acc, curr) => ({
      [curr.hash]: curr.grooming_portfolio,
      ...acc,
    }),
    {}
  )

  function updateImagesByHash(hash) {
    setSelectedImages(imageHashes[hash])
  }

  const { ref, mounted, modalOpen, openModal, closeModal, focusRef } = usePortalModal(
    team_members.map(({ hash }) => hash),
    updateImagesByHash
  )

  // Render nothing for storyblok preview
  if (!team_members?.length) return null

  const slideWidth = 210

  function moveSlidesLeft() {
    currentSlide !== 0
      ? setSlide(currentSlide - slideWidth)
      : setSlide(team_members.length * slideWidth - visibleSlides() * slideWidth)
  }

  function moveSlidesRight() {
    currentSlide <= (team_members.length - 1 - visibleSlides()) * slideWidth
      ? setSlide(currentSlide + slideWidth)
      : setSlide(0)
  }

  return (
    <>
      {mounted && modalOpen
        ? createPortal(
            <PortfolioModal
              images={selectedImages}
              setSelectedImages={setSelectedImages}
              closeModal={closeModal}
              focusRef={focusRef}
            />,
            ref.current
          )
        : null}
      <section className="lg:flex bg-gray-light bg-fixed bg-grooming-pattern">
        <div
          className={cn("mx-auto items-center flex flex-col w-full py-12 relative", {
            "max-w-4xl": visibleSlides() < 4,
            "max-w-6xl": visibleSlides() === 4,
          })}
        >
          {heading ? <h2 className="text-l1 lg:text-l2 pb-12">{heading}</h2> : null}
          <div className="flex justify-center relative overflow-hidden w-full pb-3">
            <div
              className="flex items-center sm:items-stretch transition-all duration-700"
              style={{
                transform: `translateX(-${currentSlide}px)`,
                width: `${slideWidth * visibleSlides()}px`,
              }}
              aria-live="off"
            >
              {team_members.map((member, idx) => {
                const offset = currentSlide / slideWidth
                const range = offset + visibleSlides()
                const visible = idx < range && idx >= offset

                return <GroomerCard {...{ ...member, visible }} key={`${member._uid}-portfolio`} />
              })}
            </div>
          </div>
          {visibleSlides() < team_members.length ? (
            <div>
              <button onClick={moveSlidesLeft} aria-label="Previous Slide">
                <PawprintArrow className="carousel-arrow absolute left-0 bottom-48 rotate-180 scale-75 sm:scale-100" />
              </button>
              <button onClick={moveSlidesRight} aria-label="Next Slide">
                <PawprintArrow className="carousel-arrow absolute right-0 bottom-48 scale-75 sm:scale-100" />
              </button>
            </div>
          ) : null}
        </div>
      </section>
    </>
  )
}
